import { Component, DestroyRef, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { KorviuStepsComponent } from 'shared/components/korviu-steps/korviu-steps.component';
import { FormsModule } from '@angular/forms';
import { BankInfoComponent } from './bank-info/bank-info.component';
import {
  DocumentTypes,
  IAddPaymentAccount,
  IDocument,
  UserDocument,
  UserDocumentType
} from 'shared/types/payment-account.interface';
import { NavigationService } from 'core/services/navigation.service';
import { DocumentationComponent } from './documentation/documentation.component';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageKeys } from 'shared/types/local-storage-keys.enum';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { CustomMessageService } from 'core/services/custom-message.service';
import { TranslateService } from '@ngx-translate/core';
import { paymentAccountSubmitErrorKey } from '../payment-account-error.helpers';

@Component({
  selector: 'app-add',
  standalone: true,
  imports: [
    TranslateModule,
    KorviuStepsComponent,
    FormsModule,
    BankInfoComponent,
    DocumentationComponent,
    KorviuLoaderComponent
  ],
  templateUrl: './add.component.html',
  styleUrl: './add.component.scss'
})
export class AddComponent {
  public isLoading = signal<boolean>(false);
  public step = signal<number>(0);
  private _navigationService = inject(NavigationService);
  private _paymentAccountsService = inject(PaymentAccountsService);
  private _destroyRef = inject(DestroyRef);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _messageService = inject(CustomMessageService);
  private _translateService = inject(TranslateService);

  public addPaymentAccountData: IAddPaymentAccount = {
    bankInfo: null,
    documentation: { officialIdFiles: [], taxFiles: [] }
  };

  public onBankInfoBackClick(): void {
    this._navigationService.goBack();
  }

  public onBankInfoContinueClick(): void {
    this.step.set(1);
  }

  public onDocumentationBackClick(): void {
    this.step.set(0);
  }

  public onDocumentationSubmitClick(): void {
    if (this.isLoading()) {
      return;
    }
    this.isLoading.set(true);
    this._uploadDocuments()
      .pipe(
        switchMap((documentIds: number[]) => this._addPaymentAccount(documentIds)),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (error: unknown) => {
          this._messageService.error(this._translateService.instant(paymentAccountSubmitErrorKey(error)));
        }
      });
  }

  private _addPaymentAccount(documentIds: number[]): Observable<string> {
    return this._paymentAccountsService.addPaymentAccount(this.addPaymentAccountData, documentIds).pipe(
      tap(() => {
        localStorage.setItem(LocalStorageKeys.SHOW_DOCUMENTS_RECEIVED_MODAL, 'true');
        this._router.navigate(['summary'], { relativeTo: this._route.parent });
      })
    );
  }

  private _uploadDocuments(): Observable<number[]> {
    const officialIdFiles = this.addPaymentAccountData?.documentation?.officialIdFiles || [];
    const taxFiles = this.addPaymentAccountData?.documentation?.taxFiles || [];

    const officialIDfileIDs = officialIdFiles.map(f => f.id);
    const taxfileIDs = taxFiles.map(f => f.id);

    const fileExist = (file, arr) => !!arr.find(item => item.size === file.size);
    const prefilledOfficialIds =
      this.addPaymentAccountData?.documentation?.officialId
        ?.filter((d: IDocument) => !!fileExist(d.file, officialIdFiles) && officialIDfileIDs.includes(d.id))
        .map((d: IDocument) => d.id) || [];
    const prefilledTaxIds =
      this.addPaymentAccountData?.documentation?.tax
        ?.filter((d: IDocument) => !!fileExist(d.file, taxFiles) && taxfileIDs.includes(d.id))
        .map((d: IDocument) => d.id)
        .filter(Boolean) || [];

    return this._getDocumentTypes().pipe(
      switchMap((types: UserDocumentType[]) => {
        const officialIdUserDocuments = officialIdFiles
          .filter(f => f.thumbUrl)
          ?.map((file: any) => {
            const documentType = types.find((t: UserDocumentType) => t.code === DocumentTypes.ID);
            const userDocument: UserDocument = {
              file_name: file?.name,
              blob: file?.thumbUrl,
              user_document_type_id: documentType?.id,
              user_document_status_id: 4
            };
            return userDocument;
          });
        const taxUserDocuments = taxFiles
          ?.filter(f => f.thumbUrl)
          .map((file: any) => {
            const documentType = types.find((t: UserDocumentType) => t.code === DocumentTypes.TAX);
            const userDocument: UserDocument = {
              file_name: file?.name,
              blob: file?.thumbUrl,
              user_document_type_id: documentType?.id,
              user_document_status_id: 4
            };
            return userDocument;
          });
        const uploadDocuments = [...officialIdUserDocuments, ...taxUserDocuments].map((document: UserDocument) => {
          return this._paymentAccountsService.createUserDocument(document);
        });
        return uploadDocuments?.length ? forkJoin(uploadDocuments) : of([]);
      }),
      map((documents: any[]) =>
        documents?.map((d: any) => d?.id as number).concat(prefilledOfficialIds, prefilledTaxIds)
      )
    );
  }

  private _getDocumentTypes(): Observable<UserDocumentType[]> {
    return this._paymentAccountsService.getDocumentTypes();
  }
}
